export type MapCoordinate = [number, number];
export type MapBounds = [MapCoordinate, MapCoordinate];

export interface MapViewSnapshot {
  lng: number;
  lat: number;
  zoom: number;
  bearing: number;
  pitch: number;
}

export interface ProjectedCoordinate {
  x: number;
  y: number;
}

export interface MapCameraMarker {
  setLngLat(coordinate: MapCoordinate): MapCameraMarker;
  getLngLat(): { lng: number; lat: number };
  remove(): void;
}

interface CameraRuntime {
  getCenter(): { lng: number; lat: number };
  getZoom(): number;
  getBearing(): number;
  getPitch(): number;
  getCanvas(): { clientWidth: number; clientHeight: number };
  getContainer(): { clientWidth: number; clientHeight: number };
  project(coordinate: MapCoordinate | { lng: number; lat: number }): ProjectedCoordinate;
  fitBounds(bounds: MapBounds, options: unknown): unknown;
  easeTo(options: unknown): unknown;
  setProjection?(projection: { type: 'globe' | 'mercator' }): unknown;
  setTerrain?(terrain: { source: string; exaggeration: number } | null): unknown;
  setSky?(sky: { 'atmosphere-blend': number }): unknown;
  zoomIn(): unknown;
  zoomOut(): unknown;
  rotateTo(bearing: number, options: unknown): unknown;
}

export interface MapCameraController {
  viewSnapshot(): MapViewSnapshot;
  center(): MapCoordinate;
  viewport(): { width: number; height: number };
  hash(): string;
  project(coordinate: MapCoordinate | { lng: number; lat: number }): ProjectedCoordinate;
  isVisible(coordinate: MapCoordinate, bottomInset: number): boolean;
  fitInitial(bounds: MapBounds): void;
  fitRoutes(bounds: MapBounds): void;
  fitDynamicRoute(bounds: MapBounds): void;
  locate(coordinate: MapCoordinate): ProjectedCoordinate;
  follow(coordinate: MapCoordinate, anchor: ProjectedCoordinate): void;
  setBaseProjection(terrain3d: boolean): void;
  restoreAtmosphere(): void;
  setTerrain3d(active: boolean, sourceId: string, exaggeration: number): void;
  createMarker(element: HTMLElement, coordinate: MapCoordinate): MapCameraMarker | null;
  zoomIn(): void;
  zoomOut(): void;
  resetNorth(): void;
}

function trimFixed(value: number, digits: number) {
  return value.toFixed(digits).replace(/\.?0+$/, '');
}

export function createMapCameraController(
  runtime: CameraRuntime,
  markerFactory?: (element: HTMLElement, coordinate: MapCoordinate) => MapCameraMarker | null
): MapCameraController {
  const viewSnapshot = (): MapViewSnapshot => {
    const center = runtime.getCenter();
    return {
      lng: center.lng,
      lat: center.lat,
      zoom: runtime.getZoom(),
      bearing: runtime.getBearing(),
      pitch: runtime.getPitch()
    };
  };

  return {
    viewSnapshot,
    center() {
      const center = runtime.getCenter();
      return [center.lng, center.lat];
    },
    viewport() {
      const container = runtime.getContainer();
      return { width: container.clientWidth, height: container.clientHeight };
    },
    hash() {
      const view = viewSnapshot();
      const zoom = trimFixed(view.zoom, 2);
      const lat = trimFixed(view.lat, 5);
      const lng = trimFixed(view.lng, 5);
      const bearing = view.bearing.toFixed(0);
      const pitch = view.pitch.toFixed(0);
      return pitch !== '0' || bearing !== '0'
        ? `#${zoom}/${lat}/${lng}/${bearing}/${pitch}`
        : `#${zoom}/${lat}/${lng}`;
    },
    project(coordinate) { return runtime.project(coordinate); },
    isVisible(coordinate, bottomInset) {
      const canvas = runtime.getCanvas();
      const projected = runtime.project(coordinate);
      return projected.x >= 0
        && projected.x <= canvas.clientWidth
        && projected.y >= 0
        && projected.y <= Math.max(0, canvas.clientHeight - bottomInset);
    },
    fitInitial(bounds) { runtime.fitBounds(bounds, { padding: 24, duration: 0 }); },
    fitRoutes(bounds) {
      const isMobile = runtime.getContainer().clientWidth < 780;
      runtime.fitBounds(bounds, {
        padding: isMobile ? 28 : { top: 80, right: 70, bottom: 230, left: 380 },
        duration: 800
      });
    },
    fitDynamicRoute(bounds) { runtime.fitBounds(bounds, { padding: 90, duration: 700 }); },
    locate(coordinate) {
      runtime.easeTo({ center: coordinate, zoom: Math.max(runtime.getZoom(), 12), duration: 700 });
      return runtime.project(coordinate);
    },
    follow(coordinate, anchor) {
      const container = runtime.getContainer();
      const center = { x: container.clientWidth / 2, y: container.clientHeight / 2 };
      runtime.easeTo({
        center: coordinate,
        zoom: runtime.getZoom(),
        offset: [center.x - anchor.x, center.y - anchor.y],
        duration: 700
      });
    },
    setBaseProjection(terrain3d) {
      if (!terrain3d) runtime.setProjection?.({ type: 'globe' });
    },
    restoreAtmosphere() { runtime.setSky?.({ 'atmosphere-blend': 0 }); },
    setTerrain3d(active, sourceId, exaggeration) {
      if (active) {
        runtime.setProjection?.({ type: 'mercator' });
        runtime.setTerrain?.({ source: sourceId, exaggeration });
        if (runtime.getPitch() < 80) runtime.easeTo({ pitch: 85, duration: 500 });
      } else {
        runtime.setTerrain?.(null);
        runtime.setProjection?.({ type: 'globe' });
        if (runtime.getPitch() > 0) runtime.easeTo({ pitch: 0, duration: 500 });
      }
    },
    createMarker(element, coordinate) { return markerFactory?.(element, coordinate) ?? null; },
    zoomIn() { runtime.zoomIn(); },
    zoomOut() { runtime.zoomOut(); },
    resetNorth() { runtime.rotateTo(0, { duration: 500 }); }
  };
}
